import { useResponderMap } from 'features/Responder';
import { usePersonnel } from 'features/Responder/features/Personnel';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import {
  getCurrentTimeString,
  getDurationMinutesBetweenTimes,
  getEndTimeFromStartAndDurationSeconds,
  getTotalDurationOfTour,
} from 'features/Responder/features/Schedules/features/ScheduleBuilder/utils';
import { resolveTourStartLatLng } from 'features/Responder/features/Tours';
import { useTours } from 'features/Responder/features/Tours';
import { useZones } from 'features/Responder/features/Zones';
import { usePickerField } from 'features/Responder/hooks/form fields';
import useResponderDialog from 'features/Responder/hooks/useResponderDialog';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RRule } from 'rrule';
import apiClient from 'services/_infra/apiClient';
import {
  useScheduleBuilderEditHydration,
  useScheduleBuilderTargetSelection,
  useScheduleBuilderTourHydration,
  useScheduleBuilderUnsavedChanges,
} from './hooks';
import ScheduleBuilderView from './components/ScheduleBuilderView';

const ScheduleBuilder = ({
  initialPrefill = null,
  onPersistSuccess = () => {},
  onCancelEdit = () => {},
  onUnsavedChangesChange = () => {},
}) => {
  // Support multiple occurrences: startTime and endTime as arrays
  const [startTime, setStartTime] = useState([getCurrentTimeString()]);
  const [endTime, setEndTime] = useState(['']);

  const [extraStartTimes, setExtraStartTimes] = useState([]);
  const [notAllDayTimeStart, setNotAllDayTimeStart] = useState('');
  // Store previous start/end times for restoration
  const [oldTimeSlot, setOldTimeSlot] = useState({ start: '', end: '' });
  // State for tracking validation errors
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showSubmitTooltip, setShowSubmitTooltip] = useState(false);
  const submitTooltipTimeoutRef = useRef(null);
  const scheduleScrollRef = useRef(null);
  const appliedPrefillKeyRef = useRef(null);
  const selectedTourIdRef = useRef(null);
  const selectedZoneIdRef = useRef(null);
  const { confirm: showConfirm, dialogElement } = useResponderDialog();

  // State for selected personnel
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);

  // Get data from context
  const { personnel: ctxPersonnel } = usePersonnel();
  const { tours: ctxTours, setTours, selectTour, setCenter } = useTours();
  const { setSelectedTourIds, setSelectedZoneIds: setMapSelectedZoneIds } = useResponderMap();
  const {
    zones: ctxZones,
    setActiveZoneId,
    setSelectedZoneIds: setZoneSelectedZoneIds,
  } = useZones();
  const tours = useMemo(() => (Array.isArray(ctxTours) ? ctxTours : []), [ctxTours]);
  const zones = useMemo(() => (Array.isArray(ctxZones) ? ctxZones : []), [ctxZones]);

  const getTourCenter = (tour) => resolveTourStartLatLng(tour);

  const focusTourOnMap = (tour) => {
    const centerPoint = getTourCenter(tour);
    if (!centerPoint) return;
    setCenter([centerPoint.lat, centerPoint.lng, 16]);
  };

  const { hydrateTourIfNeeded, resetPendingTourHydration } = useScheduleBuilderTourHydration({
    selectedTourIdRef,
    focusTourOnMap,
    selectTour,
    setSelectedTourIds,
    setTours,
  });

  // Schedule name state
  const [scheduleName, setScheduleName] = useState('');
  // Notes state
  const [notes, setNotes] = useState('');
  const [color, setColor] = useState(null);

  // Schedules context
  const { fetchSchedules, activeEditSchedule, setActiveEditSchedule, colorMap } = useSchedules();
  const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [rrule, setRrule] = useState(
    new RRule({
      freq: RRule.DAILY,
      interval: 1,
      count: 1,
      wkst: RRule.SU,
      tzid,
    }),
  );

  const [scheduleDuration, setScheduleDuration] = useState(0);
  const [oldScheduleDuration, setOldScheduleDuration] = useState(0);
  const [useTourDuration, setUseTourDuration] = useState(true);

  // Use only context data for personnel and tours
  const personnel = useMemo(
    () => (Array.isArray(ctxPersonnel) ? ctxPersonnel : Object.values(ctxPersonnel || {})),
    [ctxPersonnel],
  );
  // Prepare tour options for dropdown
  const tourOptions = useMemo(
    () => ['Select a tour...', ...tours.map((tour) => tour.name || `Tour ${tour.id}`)],
    [tours],
  );

  const zoneOptions = useMemo(
    () => ['Select a zone...', ...zones.map((zone) => zone.name || `Zone ${zone.id}`)],
    [zones],
  );

  useEffect(() => {
    // Only use the first start and end time to determine duration
    if (startTime && endTime) {
      const st = Array.isArray(startTime) ? startTime[0] : startTime;
      const et = Array.isArray(endTime) ? endTime[0] : endTime;
      if (
        typeof st === 'string' &&
        st.includes(':') &&
        typeof et === 'string' &&
        et.includes(':')
      ) {
        const diff = getDurationMinutesBetweenTimes(st, et);
        if (diff !== scheduleDuration) setScheduleDuration(diff);
      }
    }
  }, [endTime, scheduleDuration, startTime]);

  // Prepare personnel options for MultiSelect
  const personnelOptions = useMemo(
    () =>
      personnel.map((p) => ({
        userId: p.userId ?? p.id,
        name: p.name || p.fullName || p.username || `Personnel ${p.userId ?? p.id}`,
        email: p.email || '',
        value: p.userId ?? p.id,
      })),
    [personnel],
  );

  const handleTimeChange = (type, value) => {
    if (type === 'start') {
      setStartTime((prev) => {
        if (Array.isArray(prev)) {
          const next = [...prev];
          next[0] = value;
          return next;
        }
        return [value];
      });
      // Optionally update endTime based on duration logic if needed
      if (useTourDuration && !isZoneSelected) {
        const tourObj = tours.find((t) => t.id === selectedTour) || tours[selectedTour - 1];
        const minDurationSec = getTotalDurationOfTour(tourObj);
        if (minDurationSec > 0) {
          const nextEndTime = getEndTimeFromStartAndDurationSeconds(value, minDurationSec);
          setEndTime((prev) => {
            if (Array.isArray(prev)) {
              const next = [...prev];
              next[0] = nextEndTime;
              return next;
            }
            return [nextEndTime];
          });
        }
      }
    } else if (type === 'end') {
      setEndTime((prev) => {
        if (Array.isArray(prev)) {
          const next = [...prev];
          next[0] = value;
          return next;
        }
        return [value];
      });
    }
  };

  const {
    selectedTour,
    setSelectedTour,
    selectedZone,
    setSelectedZone,
    selectTourOption,
    selectZoneOption,
  } = useScheduleBuilderTargetSelection({
    activeEditSchedule,
    initialPrefill,
    scheduleName,
    setScheduleName,
    startTime,
    useTourDuration,
    tours,
    zones,
    setTours,
    selectedTourIdRef,
    selectedZoneIdRef,
    appliedPrefillKeyRef,
    selectTour,
    focusTourOnMap,
    setActiveZoneId,
    setZoneSelectedZoneIds,
    setMapSelectedZoneIds,
    setSelectedTourIds,
    setCenter,
    hydrateTourIfNeeded,
    getTotalDurationOfTour,
    setEndTime,
    setScheduleDuration,
  });

  const isZoneSelected = selectedZone > 0;

  useScheduleBuilderEditHydration({
    activeEditSchedule,
    tours,
    zones,
    selectedTourIdRef,
    selectedZoneIdRef,
    setSelectedTour,
    setSelectedZone,
    selectTour,
    focusTourOnMap,
    setActiveZoneId,
    setZoneSelectedZoneIds,
    setMapSelectedZoneIds,
    setSelectedTourIds,
    setRrule,
    setStartTime,
    setOldScheduleDuration,
    setOldTimeSlot,
    setEndTime,
    getTotalDurationOfTour,
    setUseTourDuration,
    setSelectedPersonnel,
    setNotes,
    setScheduleName,
    setColor,
  });

  // Validation function returns { valid: boolean, message: string|null }
  const validateTour = () => {
    if (selectedTour === 0 && selectedZone === 0) {
      return { valid: false, message: 'Please select a tour or a zone' };
    }
    if (selectedTour > 0 && selectedZone > 0) {
      return { valid: false, message: 'Please select only one target' };
    }
    if (!scheduleName.trim()) {
      return { valid: false, message: 'Please enter a schedule name' };
    }
    if (!startTime || (!endTime && !(useTourDuration && !isZoneSelected))) {
      return { valid: false, message: 'Please select a start and end time' };
    }
    if (!rrule.toString().trim()) {
      return { valid: false, message: 'Please enter a recurrence rule' };
    }
    return { valid: true, message: null };
  };

  // Validation helper functions
  const hasNameError = !scheduleName.trim();
  const hasTargetError = selectedTour === 0 && selectedZone === 0;
  const hasTimeError = !startTime || (!endTime && !(useTourDuration && !isZoneSelected));
  const isTargetSelected = selectedTour > 0 || selectedZone > 0;

  // Show error indicators when validation would fail
  const shouldShowErrorIndicators = showValidationErrors && !validateTour().valid;
  const isEndTimeEnabled = isTargetSelected && !(useTourDuration && !isZoneSelected);
  const endTimeField = usePickerField({
    disabled: !isEndTimeEnabled,
    onChange: (event) => handleTimeChange('end', event.target.value),
    closeOnChange: false,
  });
  const startTimeField = usePickerField({
    disabled: !isTargetSelected,
    onChange: (event) => handleTimeChange('start', event.target.value),
    closeOnChange: false,
    shouldAutoAdvance: isEndTimeEnabled,
    autoAdvanceRef: endTimeField.inputRef,
  });

  const currEditData = useMemo(() => {
    const selectedTourEntityId =
      selectedTour > 0 ? (tours[selectedTour - 1]?.id ?? selectedTour) : null;
    const selectedZoneEntityId =
      selectedZone > 0 ? (zones[selectedZone - 1]?.id ?? selectedZone) : null;

    const personnelIds = selectedPersonnel.map((val) => {
      const found = personnelOptions.find((p) => p.value === val);
      return found ? found.userId : val;
    });

    return {
      entity: {
        entity_id: selectedZone > 0 ? selectedZoneEntityId : selectedTourEntityId,
        type: selectedZone > 0 ? 'Zone' : 'Tour',
      },
      id: activeEditSchedule?.id || '',
      name: scheduleName,
      recurrence: rrule.toString(),
      duration: scheduleDuration * 60 * 1000,
      personnel_ids: personnelIds,
      notes,
      color,
    };
  }, [
    selectedTour,
    selectedZone,
    scheduleName,
    rrule,
    scheduleDuration,
    selectedPersonnel,
    notes,
    color,
    tours,
    zones,
    personnelOptions,
    activeEditSchedule,
  ]);

  const currentSignature = useMemo(
    () =>
      JSON.stringify({
        mode: activeEditSchedule?.id != null ? 'edit' : 'create',
        scheduleId: activeEditSchedule?.id ?? null,
        selectedTour,
        selectedZone,
        selectedPersonnel: [...(Array.isArray(selectedPersonnel) ? selectedPersonnel : [])].sort(),
        scheduleName,
        notes,
        color,
        recurrence: rrule?.toString?.() ?? '',
        scheduleDuration,
        useTourDuration,
        startTime: Array.isArray(startTime) ? startTime : [startTime],
        endTime: Array.isArray(endTime) ? endTime : [endTime],
      }),
    [
      activeEditSchedule?.id,
      color,
      endTime,
      notes,
      rrule,
      scheduleDuration,
      scheduleName,
      selectedPersonnel,
      selectedTour,
      selectedZone,
      startTime,
      useTourDuration,
    ],
  );
  const { hasUnsavedChanges, resetUnsavedChangesTracking } = useScheduleBuilderUnsavedChanges({
    activeEditScheduleId: activeEditSchedule?.id ?? null,
    currentSignature,
    onUnsavedChangesChange,
  });

  const handleScheduleTour = async () => {
    const validation = validateTour();
    if (!validation.valid) {
      setShowValidationErrors(true);
      setShowSubmitTooltip(true);
      if (submitTooltipTimeoutRef.current) {
        clearTimeout(submitTooltipTimeoutRef.current);
      }
      submitTooltipTimeoutRef.current = setTimeout(() => {
        setShowSubmitTooltip(false);
      }, 3000);
      return;
    }

    setShowValidationErrors(false);
    setShowSubmitTooltip(false);
    if (submitTooltipTimeoutRef.current) {
      clearTimeout(submitTooltipTimeoutRef.current);
      submitTooltipTimeoutRef.current = null;
    }

    // Map selectedPersonnel (which are .value) to their userId
    const personnelIds = selectedPersonnel.map((val) => {
      const found = personnelOptions.find((p) => p.value === val);
      return found ? found.userId : val;
    });

    const selectedTourEntityId =
      selectedTour > 0 ? (tours[selectedTour - 1]?.id ?? selectedTour) : null;
    const selectedZoneEntityId =
      selectedZone > 0 ? (zones[selectedZone - 1]?.id ?? selectedZone) : null;

    const schedule = {
      entity: {
        entity_id: selectedZone > 0 ? selectedZoneEntityId : selectedTourEntityId,
        type: selectedZone > 0 ? 'Zone' : 'Tour',
      },
      id: '',
      name: scheduleName,
      recurrence: rrule.toString(),
      duration: scheduleDuration * 60 * 1000,
      // start_time,
      // end_time,
      // days_of_week,
      personnel_ids: personnelIds,
      notes,
      color,
    };
    // Log or send schedule to API
    console.log('Schedule:', schedule);
    // return;

    let res;
    if (activeEditSchedule && activeEditSchedule.id) {
      // Update existing schedule
      res = await apiClient.put(`/responder/schedules/definitions/`, [
        {
          ...schedule,
          id: activeEditSchedule.id,
        },
      ]);
    } else {
      // Create new schedule
      res = await apiClient.post('/responder/schedules/definitions/', [schedule]);
    }
    console.log('Schedule response:', res);
    if (res.status === 201 || res.status === 200) {
      // Clear selections
      clearBuilderState();
      // Refresh schedules list
      fetchSchedules();
      onPersistSuccess?.(res?.data ?? res);
    } else {
      alert('Failed to schedule tour. Please try again.');
    }
  };

  const dehydrateMapSelection = useCallback(() => {
    selectTour(null);
    setCenter(null);
    setActiveZoneId(null);
    setZoneSelectedZoneIds([]);
    setMapSelectedZoneIds([]);
    setSelectedTourIds([]);
  }, [
    selectTour,
    setCenter,
    setActiveZoneId,
    setZoneSelectedZoneIds,
    setMapSelectedZoneIds,
    setSelectedTourIds,
  ]);

  const clearBuilderState = useCallback(() => {
    setSelectedTour(0);
    setSelectedZone(0);
    selectedTourIdRef.current = null;
    selectedZoneIdRef.current = null;
    resetPendingTourHydration();
    appliedPrefillKeyRef.current = null;
    setSelectedPersonnel([]);
    setScheduleName('');
    setNotes('');
    setColor(null);
    setActiveEditSchedule(null);
    dehydrateMapSelection();
    setShowValidationErrors(false);
    setShowSubmitTooltip(false);
    if (submitTooltipTimeoutRef.current) {
      clearTimeout(submitTooltipTimeoutRef.current);
      submitTooltipTimeoutRef.current = null;
    }
    resetUnsavedChangesTracking();
  }, [
    dehydrateMapSelection,
    resetPendingTourHydration,
    resetUnsavedChangesTracking,
    setActiveEditSchedule,
  ]);

  const handleClearSelection = useCallback(async () => {
    if (hasUnsavedChanges) {
      const shouldDiscard = await showConfirm({
        title: 'Discard schedule changes',
        message:
          'You have unsaved changes. Clearing the builder will discard these changes. Continue?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!shouldDiscard) return;
    }
    clearBuilderState();
  }, [clearBuilderState, hasUnsavedChanges, showConfirm]);

  const handleCancel = useCallback(async () => {
    if (hasUnsavedChanges) {
      const shouldDiscard = await showConfirm({
        title: 'Discard schedule changes',
        message: 'You have unsaved changes. Canceling will discard these changes. Continue?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!shouldDiscard) return;
    }
    dehydrateMapSelection();
    onUnsavedChangesChange?.(false);
    onCancelEdit?.();
  }, [dehydrateMapSelection, hasUnsavedChanges, onCancelEdit, onUnsavedChangesChange, showConfirm]);

  useEffect(() => {
    return () => {
      if (submitTooltipTimeoutRef.current) {
        clearTimeout(submitTooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ScheduleBuilderView
      activeEditSchedule={activeEditSchedule}
      scheduleName={scheduleName}
      setScheduleName={setScheduleName}
      shouldShowErrorIndicators={shouldShowErrorIndicators}
      hasNameError={hasNameError}
      tourOptions={tourOptions}
      selectedTour={selectedTour}
      selectTourOption={selectTourOption}
      hasTargetError={hasTargetError}
      selectedZone={selectedZone}
      zoneOptions={zoneOptions}
      selectZoneOption={selectZoneOption}
      rrule={rrule}
      setRrule={setRrule}
      startTime={startTime}
      scheduleDuration={scheduleDuration}
      endTime={endTime}
      useTourDuration={useTourDuration}
      notAllDayTimeStart={notAllDayTimeStart}
      setNotAllDayTimeStart={setNotAllDayTimeStart}
      setUseTourDuration={setUseTourDuration}
      setScheduleDuration={setScheduleDuration}
      setStartTime={setStartTime}
      setEndTime={setEndTime}
      setOldTimeSlot={setOldTimeSlot}
      setOldScheduleDuration={setOldScheduleDuration}
      oldTimeSlot={oldTimeSlot}
      oldScheduleDuration={oldScheduleDuration}
      isZoneSelected={isZoneSelected}
      tours={tours}
      getTotalDurationOfTour={getTotalDurationOfTour}
      hasTimeError={hasTimeError}
      isTargetSelected={isTargetSelected}
      startTimeField={startTimeField}
      endTimeField={endTimeField}
      extraStartTimes={extraStartTimes}
      setExtraStartTimes={setExtraStartTimes}
      notes={notes}
      setNotes={setNotes}
      colorMap={colorMap}
      color={color}
      setColor={setColor}
      handleCancel={handleCancel}
      handleClearSelection={handleClearSelection}
      showSubmitTooltip={showSubmitTooltip}
      scheduleScrollRef={scheduleScrollRef}
      handleScheduleTour={handleScheduleTour}
      currEditData={currEditData}
      dialogElement={dialogElement}
    />
  );
};

export default ScheduleBuilder;
